import { JSX, useMemo, useState } from "react";
import XmrChart, { KalmanToggle, type KalmanViewMode } from "./charts/XmrChart.tsx";
import PChart from "./charts/PChart.tsx";
import EwmaChart from "./charts/EwmaChart.tsx";
import TimeseriesLineChart from "./charts/TimeseriesLineChart.tsx";
import BoxplotChart from "./charts/BoxplotChart.tsx";
import CumBarChart from "./charts/CumbarChart.tsx";
import { computeXmr } from "../metrics/xmr";
import { computeKalman } from "../metrics/kalman";
import { computeEwma } from "../metrics/ewma";
import { computeDistributionStats } from "../metrics/distributionStats";
import type { Timeseries } from "../types";
import type { PlotChartType, EwmaConfig, StackedBarConfig, DistributionXmrConfig } from "../config/plots.ts";
import type { MeasureDef } from "../config/metrics.ts";
import { BoxplotData, ChartData, ContinuousData, DimensionSeries } from "../config/data.ts";
import { buildAxisFormatter } from "./chartFormatting";
import ChartFrame from "./ChartInspector";
import { DIMENSIONS, type DimensionId } from "../config/dimensions";

interface MetricChartProps {
  title: string;
  chart: PlotChartType;
  data: ChartData;
  measure?: MeasureDef;
  ratioSeries?: {
    numerator: Timeseries;
    denominator: Timeseries;
  };
  ewmaOptions?: EwmaConfig;
  stackedBarConfig?: StackedBarConfig;
  distributionXmrConfig?: DistributionXmrConfig;
  dimensionData?: DimensionSeries;
  grain?: "day" | "week";
}

const DIMENSION_MAP = new Map(DIMENSIONS.map(d => [d.id, d]));

export function MetricChart({
  title,
  chart,
  data,
  measure,
  ratioSeries,
  ewmaOptions,
  stackedBarConfig,
  distributionXmrConfig,
  dimensionData,
  grain,
}: MetricChartProps) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [kalmanMode, setKalmanMode] = useState<KalmanViewMode>("standard");
  // For boxplot charts with log scale, allow toggling between log and linear
  const defaultLogScale = chart === "boxplot" ? ((data as BoxplotData).logScale ?? false) : false;
  const [useLogScale, setUseLogScale] = useState(defaultLogScale);
  const axisFormatting = useMemo(() => buildAxisFormatter(measure), [measure]);
  const chartAxisFormatter = useMemo(() => {
    if (!axisFormatting.axisFormatter) return undefined;
    if (chart === "xmr-log") {
      return (value: number) => axisFormatting.axisFormatter!(Math.exp(value));
    }
    return axisFormatting.axisFormatter;
  }, [axisFormatting.axisFormatter, chart]);
  const detailFormatter = axisFormatting.tableFormatter ?? chartAxisFormatter;
  const axisName = axisFormatting.axisName;
  const chartLabel =
    chart === "xmr"
      ? "XmR chart"
      : chart === "xmr-log"
        ? "XmR chart (log)"
        : chart === "x"
          ? "X chart"
          : chart === "p"
            ? "P chart"
            : chart === "ewma"
              ? "EWMA chart"
              : chart === "line"
                ? "Line chart"
                : chart === "boxplot"
                  ? "Boxplot chart"
                  : chart === "stacked-bar"
                    ? "Stacked bar chart"
                    : chart === "distribution-xmr"
                      ? "Distribution XmR chart"
                      : chart;
  let chartContent: JSX.Element;

  const ts = data as Timeseries;
  const hasTimeseries = Array.isArray(ts?.data) && ts.data.length > 0;

  let hasKalman = false;

  if (chart === "xmr" || chart === "xmr-log") {
    if (!hasTimeseries) return null;
    const xmrData = computeXmr(ts, chart === "xmr-log" ? { transform: "log" } : undefined);
    const kalman =
      grain !== "week" ? computeKalman(xmrData.ts, { Q: 0.1 * xmrData.sigma ** 2, R: xmrData.sigma ** 2 }) : undefined;
    hasKalman = !!kalman;
    chartContent = (
      <XmrChart
        xmr={xmrData}
        kalman={kalman}
        viewMode={kalman ? kalmanMode : undefined}
        axisFormatter={chartAxisFormatter}
        axisName={axisName}
      />
    );
  } else if (chart === "x") {
    const meanSeries = "mean" in (data as ContinuousData) ? (data as ContinuousData).mean : ts;
    if (!Array.isArray(meanSeries?.data) || meanSeries.data.length === 0) return null;
    const xmrData = computeXmr(meanSeries);
    const kalman =
      grain !== "week" ? computeKalman(xmrData.ts, { Q: 0.1 * xmrData.sigma ** 2, R: xmrData.sigma ** 2 }) : undefined;
    hasKalman = !!kalman;
    chartContent = (
      <XmrChart
        xmr={xmrData}
        kalman={kalman}
        viewMode={kalman ? kalmanMode : undefined}
        axisFormatter={chartAxisFormatter}
        axisName={axisName}
      />
    );
  } else if (chart === "ewma") {
    const ewmaSeries = computeEwma(data as Timeseries, {
      lambda: ewmaOptions?.lambda ?? 0.2,
      limitMultiplier: ewmaOptions?.limitMultiplier ?? 3,
    });
    chartContent = <EwmaChart series={ewmaSeries} axisFormatter={chartAxisFormatter} axisName={axisName} />;
  } else if (chart === "line") {
    chartContent = (
      <TimeseriesLineChart series={data as Timeseries} axisFormatter={chartAxisFormatter} axisName={axisName} />
    );
  } else if (chart === "p") {
    if (!ratioSeries) {
      chartContent = <div>Missing numerator/denominator data</div>;
    } else {
      chartContent = (
        <PChart
          successes={ratioSeries.numerator}
          trials={ratioSeries.denominator}
          axisFormatter={chartAxisFormatter}
          axisName={axisName}
        />
      );
    }
  } else if (chart === "boxplot") {
    const boxplotData = data as BoxplotData;
    // Override logScale with toggle state
    const displayData: BoxplotData = { ...boxplotData, logScale: useLogScale };
    chartContent = (
      <BoxplotChart
        data={displayData}
        axisFormatter={chartAxisFormatter}
        axisName={axisName}
        selectedDate={selectedDate ?? undefined}
        onDateSelect={setSelectedDate}
      />
    );
  } else if (chart === "stacked-bar") {
    // CumBarChart has its own ChartFrame, so return it directly
    if (!dimensionData) {
      return <div>Missing dimension data for stacked bar chart</div>;
    }
    return (
      <CumBarChart
        title={title}
        data={dimensionData}
        maxDimensions={stackedBarConfig?.maxCategories ?? 10}
        measure={measure}
        axisFormatter={chartAxisFormatter}
        axisName={axisName}
      />
    );
  } else if (chart === "distribution-xmr") {
    // Distribution XmR renders two stacked XmR charts
    if (!dimensionData || !distributionXmrConfig) {
      return <div>Missing dimension data or config for distribution XmR chart</div>;
    }

    const stats = computeDistributionStats(dimensionData, distributionXmrConfig.distribution);
    const meanXmr = computeXmr(stats.meanSeries);
    const meanKalman =
      grain !== "week" ? computeKalman(meanXmr.ts, { Q: 0.1 * meanXmr.sigma ** 2, R: meanXmr.sigma ** 2 }) : undefined;
    const varianceXmr = computeXmr(stats.varianceSeries);

    const dimensionLabel =
      DIMENSION_MAP.get(distributionXmrConfig.dimension as DimensionId)?.label ?? distributionXmrConfig.dimension;
    const distType = distributionXmrConfig.distribution;

    // Build titles based on distribution type
    const meanLabel =
      distType === "log-normal" ? "Geometric Mean" : distType === "logit-normal" ? "Logistic Mean" : "Mean";
    const varianceLabel =
      distType === "log-normal" ? "Log-Variance" : distType === "logit-normal" ? "Logit-Variance" : "Variance";
    const varianceAxisName = distType === "log-normal" ? "log-var" : distType === "logit-normal" ? "logit-var" : "var";
    const fitLabel = distType === "log-normal" ? "Log-normal" : distType === "logit-normal" ? "Logit-normal" : "Normal";

    const meanTitle = `${title} ${meanLabel} by ${dimensionLabel}`;
    const varianceTitle = `${title} ${varianceLabel} by ${dimensionLabel}`;

    const fitIndicator = (
      <span
        className={`distribution-fit distribution-fit--${stats.fit.interpretation.toLowerCase()}`}
        title={`Q-Q correlation: ${stats.fit.qqCorrelation.toFixed(4)} - measures how well the data fits a ${distType} distribution`}
      >
        {fitLabel} fit: {stats.fit.qqCorrelation.toFixed(3)} ({stats.fit.interpretation})
      </span>
    );

    // Only show warning in header if fit is poor or acceptable
    const isWarning = stats.fit.interpretation === "Poor" || stats.fit.interpretation === "Acceptable";
    const headerWarning = isWarning ? fitIndicator : undefined;

    // Always show fit info in the details section
    const detailsFitInfo = (
      <div className="distribution-fit-details">
        {fitIndicator}
        <p className="distribution-fit-details__description">
          The Q-Q correlation measures how well the data fits a {distType} distribution. Values above 0.97 indicate a
          good fit.
        </p>
      </div>
    );

    return (
      <div className="distribution-xmr-container">
        <ChartFrame
          title={meanTitle}
          data={stats.meanSeries}
          chartLabel="Distribution XmR (Mean)"
          measure={measure}
          axisFormatter={chartAxisFormatter}
          headerExtra={
            <>
              {meanKalman && <KalmanToggle activeMode={kalmanMode} onModeChange={setKalmanMode} />}
              {headerWarning}
            </>
          }
          detailsHeader={detailsFitInfo}
        >
          <XmrChart
            xmr={meanXmr}
            kalman={meanKalman}
            viewMode={meanKalman ? kalmanMode : undefined}
            axisFormatter={chartAxisFormatter}
            axisName={axisName}
          />
        </ChartFrame>
        <ChartFrame
          title={varianceTitle}
          data={stats.varianceSeries}
          chartLabel="Distribution XmR (Variance)"
          measure={undefined}
          axisFormatter={undefined}
        >
          <XmrChart xmr={varianceXmr} axisFormatter={undefined} axisName={varianceAxisName} />
        </ChartFrame>
      </div>
    );
  } else {
    chartContent = <div>Unknown chart type</div>;
  }

  // For boxplot charts, append metadata to title (non-standard whiskers)
  let displayTitle = title;
  let headerToggle: JSX.Element | null = hasKalman ? (
    <KalmanToggle activeMode={kalmanMode} onModeChange={setKalmanMode} />
  ) : null;
  if (chart === "boxplot") {
    const boxplotData = data as BoxplotData;
    const { lower, upper } = boxplotData.extremeLabels;
    if (lower !== "5%" || upper !== "95%") {
      displayTitle = `${title} (${lower}–${upper})`;
    }
    // Show toggle for all boxplot charts
    headerToggle = (
      <button
        type="button"
        className={`log-scale-toggle${useLogScale ? " log-scale-toggle--active" : ""}`}
        onClick={() => setUseLogScale(prev => !prev)}
        title={useLogScale ? "Switch to linear scale" : "Switch to log scale"}
      >
        Log scale
      </button>
    );
  }

  return (
    <ChartFrame
      title={displayTitle}
      data={data}
      chartLabel={chartLabel}
      measure={measure}
      axisFormatter={detailFormatter}
      selectedDate={chart === "boxplot" ? selectedDate : undefined}
      onDateSelect={chart === "boxplot" ? setSelectedDate : undefined}
      headerExtra={headerToggle}
    >
      {chartContent}
    </ChartFrame>
  );
}

export default MetricChart;
